/**
 * Tier2Details Component
 *
 * Displays Tier 2 enrichment results: code hints and subtask proposals.
 * Both arrive together from the same background processing, so they're
 * combined into a single component.
 */

import {
  Component,
  For,
  Show,
  createEffect,
  createSignal,
  createUniqueId,
  on,
  onCleanup,
} from "solid-js";
import type { CodeHint, SubtaskProposal } from "../../api";

/** Result sets this small open on arrival instead of staying collapsed. */
const AUTO_EXPAND_MAX_ITEMS = 3;

/** How long the "new" badge stays up after content arrives. */
const ATTENTION_MS = 4000;

interface CodeHintsProps {
  hints: CodeHint[];
}

const CodeHints: Component<CodeHintsProps> = (props) => {
  return (
    <Show when={props.hints.length > 0}>
      <div>
        <div class="text-xs text-ink-3 mb-1.5 flex items-center gap-1">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
          </svg>
          Related files
        </div>
        <div class="space-y-1">
          <For each={props.hints}>
            {(hint) => (
              <a
                href={hint.github_url}
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 px-2 py-1 rounded bg-surface border border-line hover:border-line-strong transition-colors text-sm"
                title={hint.reason}
              >
                <svg
                  class="w-4 h-4 text-ink-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span class="truncate text-ink-2">{hint.file_path}</span>
                <span class="text-xs text-ink-3 flex-shrink-0">
                  {Math.round(hint.confidence * 100)}%
                </span>
              </a>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
};

interface SubtaskProposalsProps {
  proposals: SubtaskProposal[];
  /** Called when selection changes with the currently selected subtasks */
  onSelectionChange?: (subtasks: SubtaskProposal[]) => void;
}

const SubtaskProposals: Component<SubtaskProposalsProps> = (props) => {
  const [selected, setSelected] = createSignal<Set<number>>(new Set());

  const toggleSelection = (index: number) => {
    const current = new Set(selected());
    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
    }
    setSelected(current);

    const selectedSubtasks = props.proposals.filter((_, i) => current.has(i));
    props.onSelectionChange?.(selectedSubtasks);
  };

  return (
    <Show when={props.proposals.length > 0}>
      <div>
        <div class="text-xs text-ink-3 mb-1.5">Suggested subtasks</div>
        <div class="space-y-1 mb-2">
          <For each={props.proposals}>
            {(proposal, index) => {
              const isSelected = () => selected().has(index());
              return (
                <div
                  class={`
                    flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors
                    ${
                      isSelected()
                        ? "bg-sunken border-ink"
                        : "bg-surface border-line hover:border-line-strong"
                    }
                  `}
                  onClick={() => toggleSelection(index())}
                >
                  <input
                    type="checkbox"
                    checked={isSelected()}
                    class="mt-0.5 w-4 h-4 accent-ink rounded border-line"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelection(index())}
                  />
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-ink">
                      {proposal.title}
                    </div>
                    <div class="text-xs text-ink-3 truncate">
                      {proposal.rationale}
                    </div>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
        <Show when={selected().size > 0}>
          <p class="text-xs text-ink-3 mt-1">
            {selected().size} subtask{selected().size > 1 ? "s" : ""} will be
            created with the task
          </p>
        </Show>
      </div>
    </Show>
  );
};

export interface Tier2DetailsProps {
  /** Code hints from Tier 2 analysis */
  codeHints: CodeHint[];
  /** Subtask proposals from Tier 2 analysis */
  subtaskProposals: SubtaskProposal[];
  /** Callback when subtask selection changes */
  onSubtasksSelectionChange?: (subtasks: SubtaskProposal[]) => void;
}

export const Tier2Details: Component<Tier2DetailsProps> = (props) => {
  const [expanded, setExpanded] = createSignal(false);
  const [userToggled, setUserToggled] = createSignal(false);
  const [isNew, setIsNew] = createSignal(false);
  const contentId = createUniqueId();

  const itemCount = () =>
    props.codeHints.length + props.subtaskProposals.length;

  const hasContent = () => itemCount() > 0;

  let attentionTimer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(attentionTimer));

  // Content arrives asynchronously from Tier 2 polling, so the expansion
  // decision has to react to the counts rather than be read once at mount.
  // Once the user has taken a side, their choice wins over the heuristic.
  createEffect(
    on(itemCount, (count, previous) => {
      if (count === 0 || count === previous) return;

      if (count <= AUTO_EXPAND_MAX_ITEMS && !userToggled()) {
        setExpanded(true);
      }

      setIsNew(true);
      clearTimeout(attentionTimer);
      attentionTimer = setTimeout(() => setIsNew(false), ATTENTION_MS);
    }),
  );

  const toggle = () => {
    setUserToggled(true);
    clearTimeout(attentionTimer);
    setIsNew(false);
    setExpanded(!expanded());
  };

  const contentSummary = () => {
    const parts: string[] = [];
    if (props.codeHints.length > 0) {
      parts.push(
        `${props.codeHints.length} file${props.codeHints.length > 1 ? "s" : ""}`,
      );
    }
    if (props.subtaskProposals.length > 0) {
      parts.push(
        `${props.subtaskProposals.length} subtask${props.subtaskProposals.length > 1 ? "s" : ""}`,
      );
    }
    return parts.join(", ");
  };

  return (
    <Show when={hasContent()}>
      <div
        class={`pt-2 border-t border-line ${isNew() ? "animate-fade-in" : ""}`}
      >
        {/* Collapsible trigger */}
        <button
          type="button"
          class="w-full flex items-center justify-between text-xs text-ink-2 hover:text-ink transition-colors py-1"
          aria-expanded={expanded()}
          aria-controls={contentId}
          onClick={toggle}
        >
          <span class="flex items-center gap-1.5">
            <svg
              class="w-3 h-3 text-signal-magenta"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
              />
            </svg>
            <span class="font-medium">Code analysis</span>
            <span class="text-ink-3">({contentSummary()})</span>
            <Show when={isNew()}>
              <span class="px-1.5 rounded-full bg-signal-magenta-tint text-signal-magenta text-[10px] font-semibold uppercase tracking-wide">
                New
              </span>
            </Show>
          </span>
          <svg
            class={`w-4 h-4 transition-transform duration-200 ${expanded() ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Preview of the top hint, so collapsed content still says something */}
        <Show when={!expanded() && props.codeHints.length > 0}>
          <div
            data-testid="tier2-preview"
            class="flex items-center gap-1.5 text-xs text-ink-3 py-0.5 pl-[18px]"
          >
            <span class="truncate text-ink-3">
              {props.codeHints[0].file_path}
            </span>
            <Show when={props.codeHints.length > 1}>
              <span class="flex-shrink-0">
                +{props.codeHints.length - 1} more
              </span>
            </Show>
          </div>
        </Show>

        {/* Collapsible content */}
        <div
          id={contentId}
          class={`
            transition-all duration-200 ease-out overflow-hidden
            ${expanded() ? "max-h-[500px] opacity-100 mt-2 visible" : "max-h-0 opacity-0 invisible"}
          `}
        >
          <div class="space-y-3">
            <CodeHints hints={props.codeHints} />
            <SubtaskProposals
              proposals={props.subtaskProposals}
              onSelectionChange={props.onSubtasksSelectionChange}
            />
          </div>
        </div>
      </div>
    </Show>
  );
};
